import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, FileDown, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { SectionTitle, Field, LoadingState, ErrorState, StatusBadge, Badge } from "@/components/common";
import { useGarage } from "@/context/GarageContext";
import { usePortal } from "@/pages/portal/PortalLayout";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, downloadFile } from "@/lib/api";
import { fmtCents } from "@/lib/format";
import { t } from "@/i18n";

function ReportConfig({ cfg, currency, onSaved }) {
  const [f, setF] = useState({ enabled: cfg.enabled, price: (cfg.price_cents / 100).toFixed(2) });
  const save = () => api.put("/garage/history-report", { enabled: f.enabled, price: Number(f.price || 0) }).then(() => { toast.success(t("b9.connect.saved")); onSaved(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-4" data-testid="history-report-config">
      <label className="flex items-center gap-3 text-sm font-medium"><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="history-report-enabled" /> {t("b9.connect.enabled")}</label>
      <Field label={`${t("b9.connect.price")} (${currency})`}><Input type="number" min={0} step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="w-40" data-testid="history-report-price" /></Field>
      <Button onClick={save} data-testid="history-report-save">{t("common.save")}</Button>
    </div>
  );
}

export function PaymentsSettings() {
  const [params] = useSearchParams();
  const { data, error, loading, reload } = useFetch("/garage/payments-connect");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (params.get("connect") === "return") toast.success(t("b9.connect.returned")); }, [params]);
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const c = data.connect;
  const status = !c.account_id ? "notConnected" : c.charges_enabled ? "ready" : "pending";
  const onboard = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/garage/payments-connect/onboard", { origin_url: window.location.origin });
      window.location.href = r.url;
    } catch (e) {
      toast.error(errMsg(e), { duration: 10000 });
      setBusy(false);
    }
  };
  return (
    <div className="space-y-10" data-testid="payments-settings">
      <div>
        <SectionTitle hint={t("b9.connect.hint")}>{t("b9.connect.title")}</SectionTitle>
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-4">
          <CreditCard className="h-5 w-5 text-slate-600" />
          <Badge tone={status === "ready" ? "green" : status === "pending" ? "amber" : "slate"} testId="connect-status">{t(`b9.connect.${status}`)}</Badge>
          {c.account_id && <span className="font-mono text-xs text-slate-500" data-testid="connect-account-id">{t("b9.connect.account")}: {c.account_id}</span>}
          {status !== "ready" && <Button className="ml-auto" disabled={busy} onClick={onboard} data-testid="connect-onboard-button">{c.account_id ? t("b9.connect.continue") : t("b9.connect.connect")}</Button>}
        </div>
      </div>
      <div>
        <SectionTitle hint={t("b9.connect.reportHint")}>{t("b9.connect.reportTitle")}</SectionTitle>
        <ReportConfig cfg={data.history_report} currency={data.currency} onSaved={reload} />
      </div>
    </div>
  );
}

export function StaffHistoryReport({ vehicle }) {
  const { isManager, readOnly } = useGarage();
  const { data, reload } = useFetch(`/vehicles/${vehicle.id}/history-report`);
  if (!data) return null;
  const mark = () => api.post(`/vehicles/${vehicle.id}/history-report/mark-paid`, {}).then(() => { toast.success(t("b9.report.markedPaid")); reload(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`staff-history-report-${vehicle.plate}`}>
      <Button size="sm" variant="outline" disabled={!data.available} title={data.available ? "" : t("b11.noJobs")} onClick={() => downloadFile(`/vehicles/${vehicle.id}/history-report.pdf`).catch((e) => toast.error(errMsg(e)))} data-testid={`staff-history-pdf-${vehicle.plate}`}><FileDown className="mr-1.5 h-4 w-4" />{t("b9.report.staffDownload")}</Button>
      {!data.available && <span className="text-xs text-slate-500" data-testid={`staff-history-unavailable-${vehicle.plate}`}>{t("b11.noJobs")}</span>}
      {data.available && data.config.enabled && (data.unlocked
        ? <StatusBadge status="paid" testId={`staff-history-unlocked-${vehicle.plate}`} />
        : isManager && !readOnly && data.config.price_cents > 0 && <Button size="sm" variant="ghost" onClick={mark} data-testid={`staff-history-mark-paid-${vehicle.plate}`}>{t("b9.report.markPaid")}</Button>)}
    </div>
  );
}

export function PortalHistoryReport({ slug, vehicle }) {
  const { garage } = usePortal();
  const { data, reload } = useFetch(`/portal/${slug}/vehicles/${vehicle.id}/history-report`);
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  if ((data.enabled || data.unlocked) && !data.available) return <p className="text-xs text-slate-500" data-testid={`portal-history-nojobs-${vehicle.plate}`}>{t("b9.report.title")}: {t("b11.noJobs")}</p>;
  if (!data.enabled && !data.unlocked) return <p className="text-xs text-slate-500" data-testid={`portal-history-unavailable-${vehicle.plate}`}>{t("b10.report.notAvailable")}</p>;
  const price = fmtCents(data.price_cents, data.currency);
  const buy = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/portal/${slug}/vehicles/${vehicle.id}/history-report/checkout`, { origin_url: window.location.origin });
      if (r.checkout_url) window.location.href = r.checkout_url;
      else { reload(); setBusy(false); }
    } catch (e) {
      toast.error(errMsg(e), { duration: 10000 });
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`portal-history-report-${vehicle.plate}`}>
      <p className="flex items-center gap-2 font-semibold text-slate-900">{data.unlocked ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Lock className="h-4 w-4 text-slate-500" />}{t("b9.report.title")}</p>
      <p className="mt-1 text-xs text-slate-600">{t("b9.report.hint", { garage: garage.name })} {!data.unlocked && data.price_cents > 0 && t("b9.report.price", { price })}</p>
      {!data.unlocked && !data.online_payment && data.price_cents > 0 && <p className="mt-2 text-xs text-amber-800" data-testid={`portal-history-offline-${vehicle.plate}`}>{t("b9.report.offline")}</p>}
      <div className="mt-3">
        {data.unlocked
          ? <Button size="sm" onClick={() => downloadFile(`/portal/${slug}/vehicles/${vehicle.id}/history-report.pdf`).catch((e) => toast.error(errMsg(e)))} style={{ background: "var(--brand)", color: "var(--brand-contrast)" }} data-testid={`portal-history-download-${vehicle.plate}`}><FileDown className="mr-1.5 h-4 w-4" />{t("b9.report.download")}</Button>
          : data.online_payment && <Button size="sm" disabled={busy} onClick={buy} style={{ background: "var(--brand)", color: "var(--brand-contrast)" }} data-testid={`portal-history-buy-${vehicle.plate}`}><CreditCard className="mr-1.5 h-4 w-4" />{t("b9.report.buy", { price })}</Button>}
      </div>
    </div>
  );
}

// Handles the return from Stripe Checkout (?report_session=...) on the portal vehicles tab.
export function ReportPaymentReturn({ slug }) {
  const [params, setParams] = useSearchParams();
  const session = params.get("report_session");
  useEffect(() => {
    if (!session) return;
    let tries = 0;
    const check = () => api.get(`/portal/${slug}/history-report/status/${session}`).then((r) => {
      if (r.data.status === "paid") { toast.success(t("b9.report.paid")); setParams({ tab: "vehicles" }); }
      else if (++tries < 6) setTimeout(check, 2000);
      else { toast.error(t("b9.report.notPaid")); setParams({ tab: "vehicles" }); }
    }).catch((e) => { toast.error(errMsg(e)); setParams({ tab: "vehicles" }); });
    toast.message(t("b9.report.paying"));
    check();
  }, [session, slug, setParams]);
  return null;
}
